import * as React from 'react';
import { connect } from 'react-redux';
import { ApplicationState } from '../../store';
import { BomTemplatesManager } from '../../store/Managers/BomTemplatesManager';
import { TeamRole } from '../../store/Models/UserContext';
import TowStarter, { DataSourceOptionType } from '../../tow/TowStarter';
import { ToxnotComponentProperties } from '../core/BaseApplicationComponents/ToxnotComponent';
import { ToxnotSignInRequiredComponent } from '../core/BaseApplicationComponents/ToxnotSignInRequiredComponent';
import InviteSuppliersCore from './InviteSuppliersCore';

import './styles.css';
import { RegulationManager } from '../../store/Managers/RegulationManager';

interface StartReportState {
    reportGroupIndex: number;
    selectedReportType: ReportTypes | null;
    dataSourceOption: DataSourceOptionType | null;
    selectedRegulations: { [key: string]: { regulationId: string; name: string } };
}

interface ReportMetadata {
    localizedName: string | JSX.Element;
    localizedDescription: string | JSX.Element;
}

enum ReportTypes {
    ComplianceReport = "reach-rohs-statement",
    RedListLetter = "compliance-letter-lbc",
    Declare = "declare",
    Hpd = "hpd",
    Leed = "leed",
    /*C2c = "c2c-report",*/
    Pcds = "pcds"
}

// ===============================================================================

//
// Sub start component for starting a report.
//
class StartReport extends ToxnotSignInRequiredComponent<ToxnotComponentProperties, StartReportState> {
    private _shouldScrollToBottom: boolean;
    private _reportTypeToMetadata: { [reportType: string]: ReportMetadata };
    private _towStarter: TowStarter;
    private _nextStepsOptionToFriendlyButtonName: { [option: string]: string };

    // ===============================================================================

    protected override getDisallowedRoles() {
        return [TeamRole.Marketing];
    }

    // ===============================================================================

    public constructor(props: ToxnotComponentProperties) {
        super(props);

        this._shouldScrollToBottom = false;

        this.state = {
            reportGroupIndex: 0,
            selectedReportType: null,
            dataSourceOption: null,
            selectedRegulations: {},
        };

        this._towStarter = new TowStarter();

        this._reportTypeToMetadata = {
            //[ReportTypes.C2c]: {
            //    localizedName: <React.Fragment>Cradle 2 Cradle Certified&trade;</React.Fragment>,
            //    localizedDescription: <React.Fragment>Generate an MBDC Material Health assessment that can be submitted for Cradle 2 Cradle Certified&trade;</React.Fragment>
            //},
            [ReportTypes.ComplianceReport]: {
                localizedName: this.localizeString("Compliance Report"),
                localizedDescription: this.localizeString("Summarize your California Proposition 65, REACH, and RoHS compliance determinations in a document you can share with customers.")
            },
            //"compliance-letter": {
            //    localizedName: this.localizeString("Regulatory Screening Letter"),
            //    localizedDescription: this.localizeString("Screen your product for substances on regulatory lists.")
            //},
            [ReportTypes.Declare]: {
                localizedName: <img src="/images/declare-logo-super-tiny.png" />,
                localizedDescription: this.localizeString("Promote your sustainability & transparency by publishing a Declare Label.")
            },
            [ReportTypes.RedListLetter]: {
                localizedName: this.localizeString("LBC 4.0 Red List Letter"),
                localizedDescription: this.localizeString("Disclose any substances on the Living Building Challenge Red List, or declare there are none.")
            },
            [ReportTypes.Hpd]: {
                localizedName: "HPD",
                localizedDescription: this.localizeString("Create a comprehensive Health Product Declaration on sustainability & hazards.")
            },
            [ReportTypes.Leed]: {
                localizedName: `LEED ${this.localizeString("Disclosure/Optimization")}`,
                localizedDescription: this.localizeString("Report for LEED v4 credit.")
            },
            [ReportTypes.Pcds]: {
                localizedName: this.localizeString("Product Circularity Data Sheet"),
                localizedDescription: this.localizeString("Summarize & promote the circular attributes of your products & materials.")
            }
        };

        this._nextStepsOptionToFriendlyButtonName = {
            [DataSourceOptionType.Import]: this.localizeString("Import File(s)"),
            [DataSourceOptionType.SelectSingle]: this.localizeString("Select Product/Material"),
            [DataSourceOptionType.SelectMultiple]: this.localizeString("Select Products/Materials"),
        }
    }

    // ===============================================================================

    public componentDidUpdate() {
        if (this._shouldScrollToBottom && $("#uat-banner")?.length == 0) {
            $("#scroll-end-target")[0].scrollIntoView({ behavior: "smooth", block: "end" });

            this._shouldScrollToBottom = false;
        }
    }

    // ===============================================================================

    private selectedReportTypeHasRegulationOptions() {
        return this.state.selectedReportType === ReportTypes.ComplianceReport;
    }

    // ===============================================================================

    private static supportsMultiSelect(reportType: ReportTypes) {
        switch (reportType) {
            case ReportTypes.ComplianceReport:
                return true;
            default:
                return false;
        }
    }

    // ===============================================================================

    public renderConcreteComponent() {
        const regulationIdToLocalizedName: { [regulationId: string]: string } = {
            [RegulationManager.CaProp65RegulationId]: "California Proposition 65",
            [RegulationManager.ConflictMineralsRegulationId]: this.localizeString("Conflict Minerals - Basic (Legacy)"),
            [RegulationManager.ConflictMineralsV2RegulationId]:"Conflict Minerals (CMRT)",
            [RegulationManager.ConflictMineralsTemplateRegulationId]: this.localizeString("Conflict Minerals - Official Template (Legacy)"),
            [RegulationManager.ReachSvhcCandidateRegulationId]: "EU REACH SVHC Candidate List & SCIP",
            [RegulationManager.RohsRegulationId]: "EU RoHS"
        };

        return (
            <div id="start-report-content" className="start-content">
                <div className="full-content">
                    <div className="start-subcontent-heading">
                        {this.localizeString("Create a Compliance or Sustainability Report")}
                    </div>
                    <p className="start-subcontent-content-container">
                        {this.localizeString("What type of report do you want to start?")}
                    </p>
                    <div className="start-subcontent-content-container">
                        <div className="selectors-container">
                            {Object.values(ReportTypes).filter(reportType => reportType !== ReportTypes.Declare).map((reportType, reportTypeIndex) => (
                                <div key={reportTypeIndex} className={("selector-div " + ((this.state.selectedReportType === reportType) ? "selected" : ""))} onClick={e => this.reportTypeSelector_click(reportType)}>
                                    <div className="title">{this._reportTypeToMetadata[reportType].localizedName}</div>
                                    <div className="description">{this._reportTypeToMetadata[reportType].localizedDescription}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    {this.selectedReportTypeHasRegulationOptions() &&
                        <React.Fragment>
                            <p className="start-subcontent-content-container">
                                {this.localizeString("What regulations do you want to report on?")}
                            </p>
                            <div className="form-group start-subcontent-content-container">
                                {
                                    [
                                    RegulationManager.CaProp65RegulationId,
                                    RegulationManager.ConflictMineralsRegulationId,
                                    RegulationManager.ConflictMineralsV2RegulationId,
                                    RegulationManager.ConflictMineralsTemplateRegulationId,
                                    RegulationManager.ReachSvhcCandidateRegulationId,
                                    RegulationManager.RohsRegulationId
                                    ]
                                    .map((regulationId, index) => {
                                        const complianceEnabled = this.props.appServices.userContext!.userCapabilities.productComplianceEnabled;
                                        const isComplianceReport = this.state.selectedReportType === "reach-rohs-statement";
                                        const isDisabled = isComplianceReport && !complianceEnabled && (regulationId !== "rohs");

                                        return (
                                            <div key={index} className={`checkbox ${(isDisabled ? "disabled" : "")}`}>
                                                <label>
                                                    <input type="checkbox" checked={!!this.state.selectedRegulations[regulationId]} onChange={e => this.regulationCheckbox_change(regulationId, regulationIdToLocalizedName[regulationId])} disabled={isDisabled} /> {regulationIdToLocalizedName[regulationId]}
                                                </label>
                                            </div>
                                        );
                                    })
                                }
                            </div>
                        </React.Fragment>
                    }
                    {this.state.selectedReportType !== null && (!this.selectedReportTypeHasRegulationOptions() || (Object.keys(this.state.selectedRegulations).length > 0)) &&
                        <React.Fragment>
                            <p className="start-subcontent-content-container">
                                {this.localizeString("Where is the data you would like to report on?")}
                            </p>
                            <div className="start-subcontent-content-container">
                                <div className="selectors-container">
                                    <div key={0} className={("selector-div " + ((this.state.dataSourceOption === DataSourceOptionType.SelectSingle) ? "selected" : ""))} onClick={e => this.selectNextStepsOption_onClick(DataSourceOptionType.SelectSingle)}>
                                        <div className="title">{this.localizeString("3E Exchange Library - Single Product/Material")}</div>
                                        <div className="description">{this.localizeString("Select a product or material in your 3E Exchange Library.")}</div>
                                    </div>
                                    {StartReport.supportsMultiSelect(this.state.selectedReportType) &&
                                        <div key={1} className={("selector-div " + ((this.state.dataSourceOption === DataSourceOptionType.SelectMultiple) ? "selected" : ""))} onClick={e => this.selectNextStepsOption_onClick(DataSourceOptionType.SelectMultiple)}>
                                            <div className="title">{this.localizeString("3E Exchange Library - Multiple Products/Materials")}</div>
                                            <div className="description">{this.localizeString("Select multiple products or materials in your 3E Exchange Library.")}</div>
                                        </div>
                                    }
                                    <div key={2} className={("selector-div " + ((this.state.dataSourceOption === DataSourceOptionType.Import) ? "selected" : ""))} onClick={e => this.selectNextStepsOption_onClick(DataSourceOptionType.Import)}>
                                        <div className="title">{this.localizeString("In a Spreadsheet")}</div>
                                        <div className="description">{this.localizeString("Import the data for your report first.")}</div>
                                    </div>
                                    <div key={3} className={("selector-div " + ((this.state.dataSourceOption === DataSourceOptionType.NeedSupplyChainData) ? "selected" : ""))} onClick={e => this.selectNextStepsOption_onClick(DataSourceOptionType.NeedSupplyChainData)}>
                                        <div className="title">{this.localizeString("I need to ask my suppliers for data")}</div>
                                        <div className="description">{this.localizeString("Request Product & Material Passports, an easy way to get supply chain data. Your suppliers can use 3E Exchange for free to fill out Passports using a step-by-step guide.")}</div>
                                    </div>
                                </div>
                                {this.state.dataSourceOption === DataSourceOptionType.Import && this.state.selectedReportType !== null &&
                                    this.renderTemplateOptions(this.state.selectedReportType)
                                }
                            </div>
                        </React.Fragment>
                    }
                    {this.state.dataSourceOption !== DataSourceOptionType.NeedSupplyChainData &&
                        <p className="start-subcontent-content-container">
                            {(this.state.dataSourceOption !== null) && ((this.state.selectedReportType !== "reach-rohs-statement") || (Object.keys(this.state.selectedRegulations).length > 0)) &&
                                <button type="button" className="btn btn-toxnot-complement" onClick={e => this.startWorkflowButton_click()}>{ this._nextStepsOptionToFriendlyButtonName[this.state.dataSourceOption] ?? this.localizeString("Start Workflow")} <span className="glyphicon glyphicon-chevron-right"></span></button>
                            }
                        </p>
                    }
                    {this.state.dataSourceOption === DataSourceOptionType.NeedSupplyChainData && 
                        <>
                            <p className="start-subcontent-content-container">
                                {this.localizeString("You can request data from your suppliers by sending them part names you would like more data on.")}
                            </p>
                            <p className="start-subcontent-content-container">
                                {this.localizeString("Not sure what you need? Reach out in our in-app chat to talk to a 3E Exchange Team Member.")}
                            </p>
                            <div className="start-subcontent-content-container">
                                <div id="start-report-invite-suppliers-container">
                                    <InviteSuppliersCore shouldMakeTnSection={false} />
                                </div>
                            </div>
                        </>
                    }
                    <div id="scroll-end-target"></div>
                </div>
            </div>
        );
    }

    // ===============================================================================

    private renderTemplateOptions(selectedReportType: string) {
        let body: JSX.Element | null;

        const templateNames = BomTemplatesManager.getImportTemplatesForReportType(selectedReportType, this.props.appServices);

        if (templateNames.length === 1) {
            body = <a href={`/files/BomTemplates/${templateNames[0].filename}`} className="btn btn-default">{this.localizeString("Download Import Template")}</a>
        }

        else if (templateNames.length > 1) {
            body = (
                <div className="dropdown">
                    <button className="btn btn-default dropdown-toggle" type="button" id="bom-templates" data-toggle="dropdown" aria-haspopup="true" aria-expanded="true">
                        {this.localizeString("Download Import Template")} <span className="caret"></span>
                    </button>
                    <ul className="dropdown-menu" aria-labelledby="bom-templates">
                        {templateNames.map((t, i) => (
                            <li key={i}><a href={`/files/BomTemplates/${t.filename}`}>{t.localizedDescription}</a></li>
                        ))}
                    </ul>
                </div>
            );
        }
        else {
            body = null;
        }

        return body;
    }

    // ===============================================================================

    private startComplianceReport = async () => {
        const success = await this._towStarter.startTow(
            this.state.dataSourceOption!,
            "ComplianceReport",
            (inception, activeStep) => ({
                inception: inception,
                activeStep: activeStep,
                importMode: "SingleProductSpreadsheet",
                singleResultInventoryId: null,
                multiResultInventoryIds: null,
                importId: null,
                regulations: this.state.selectedRegulations,
                useStreamlinedGetStartedImportUi: true
            }));

        if (!success) {
            this.props.appServices.displayApiErrorMessage();
        }
    }

    // ===============================================================================

    private async startDeclareOrHpd(workflowType: "Declare" | "HPD") {
        const success = await this._towStarter.startTow(
            this.state.dataSourceOption!,
            workflowType,
            (inception, activeStep) => ({
                inception: inception,
                activeStep: activeStep,
                importMode: "SingleProductSpreadsheet",
                singleResultInventoryId: null,
                multiResultInventoryIds: null,
                importId: null,
                mode: "TBD",
                thirdPartyRequested: false,
                useStreamlinedGetStartedImportUi: true
            }));

        if (!success) {
            this.props.appServices.displayApiErrorMessage();
        }
    }

    // ===============================================================================

    private startDeclareLabel = async () => {
        await this.startDeclareOrHpd("Declare");
    }

    // ===============================================================================

    private startHpd = async () => {
        await this.startDeclareOrHpd("HPD");
    }

    // ===============================================================================

    private startGenericReport = async (reportType: string, reportContext: any | null = null, customDataPageId: string | null = null) => {
        const success = await this._towStarter.startTow(
            this.state.dataSourceOption!,
            "GenericReport",
            (inception, activeStep) => ({
                inception: inception,
                activeStep: activeStep,
                importMode: "SingleProductSpreadsheet",
                singleResultInventoryId: null,
                multiResultInventoryIds: null,
                importId: null,
                thirdPartyRequested: false,
                reportType: reportType,
                reportContext: reportContext,
                customDataPageId: customDataPageId,
                useStreamlinedGetStartedImportUi: true
            }));

        if (!success) {
            this.props.appServices.displayApiErrorMessage();
        }
    }

    // ===============================================================================

    //
    // UI Events
    //

    // ===============================================================================

    private reportTypeSelector_click = (reportType: ReportTypes) => {
        const complianceEnabled = this.props.appServices.userContext!.userCapabilities.productComplianceEnabled;

        let newSelectedRegulations = { ...this.state.selectedRegulations };

        if (!complianceEnabled && (reportType === ReportTypes.ComplianceReport)) {
            const rohRegulationOnly: { [key: string]: { regulationId: string; name: string } } = {};
            Object.values(newSelectedRegulations).forEach(reg => {
                if (reg.regulationId === "rohs") {
                    rohRegulationOnly[reg.regulationId] = reg;
                }
            });
            newSelectedRegulations = rohRegulationOnly;
        }

        this._shouldScrollToBottom = true;

        this.setState({
            selectedReportType: reportType,
            selectedRegulations: newSelectedRegulations
        });
    }

    // ===============================================================================

    private regulationCheckbox_change = (regulationId: string, name: string) => {
        this._shouldScrollToBottom = true;
        const newSelectedRegulations = { ...this.state.selectedRegulations };
        if (newSelectedRegulations[regulationId]) {
            delete newSelectedRegulations[regulationId];
        } else {
            newSelectedRegulations[regulationId] = { regulationId, name };
        }
        this.setState({
            selectedRegulations: newSelectedRegulations
        });
    }

    // ===============================================================================

    private startWorkflowButton_click = () => {
        const reportTypeToHandlerMap: { [reportType: string]: () => Promise<void> } = {
            [ReportTypes.ComplianceReport]: this.startComplianceReport,
            [ReportTypes.Declare]: this.startDeclareLabel,
            //"declare-materials": this.startDeclareLabel,
            [ReportTypes.Hpd]: this.startHpd,
            //"hpd-material-threshold": this.startHpd,
            //"hpd-simple": this.startHpd,
            //"compliance-letter": async () => await this.startGenericReport("compliance-letter", { regulations: this.state.selectedRegulations }),
            [ReportTypes.RedListLetter]: async () => await this.startGenericReport("compliance-letter-lbc"),
            [ReportTypes.Leed]: async () => await this.startGenericReport("leed", null, "standard_page_leed"),
            /*[ReportTypes.C2c]: async () => await this.startGenericReport("c2c-report", null, "standard_page_c2c"),*/
            [ReportTypes.Pcds]: async () => await this.startGenericReport("pcds", null, "standard_page_circularity"),
        };

        const handler = reportTypeToHandlerMap[this.state.selectedReportType!];

        if (!handler) {
            throw new Error(`Unknown report type - ${this.state.selectedReportType}`);
        }

        this.props.appServices.trackEvent("get-started-start-report-workflow-clicked");
        this.props.appServices.trackEvent(`get-started-start-report-workflow-${this.state.selectedReportType!}-clicked`);

        handler();
    }

    // ===============================================================================

    private selectNextStepsOption_onClick = (nextStepOption: DataSourceOptionType) => {
        this._shouldScrollToBottom = true;

        this.setState({
            dataSourceOption: nextStepOption
        });
    }
}

// ===============================================================================

export default connect(
    (state: ApplicationState) => ({ appServices: state.appServices })
)(StartReport as any);
